import {
	checkHasApprovalHierarchy,
	compareStrings,
	nullSafeGet,
	nullSafeGetOrElse,
} from './DataAccessUtils';
import { getContactName } from './DataFormatterUtils';
import { infinityButNumber } from '../constants/general';

/**
 * FE mirror of the backend `EntityRequestApprovalUtil.scala` — tier bookkeeping for the Suggested
 * Entities (entity add request) approval hierarchy.
 *
 * Entity requests carry no monetary total, so the amount-based rules the invoice / quote helpers in
 * ApprovalHierarchyUtils implement do not apply here. A request is approved once the **highest** tier
 * holding approvers is satisfied, which leaves the tiers below it optional — the cascade an invoice
 * gets from a senior approver covering every amount beneath them, expressed for an entity with no
 * amount to cover. A tier is satisfied by all of its approvers when `approvedByType` is `all` and by
 * any one of them otherwise. Tiers are ordered by `amountUpto` then numeric `label`, matching the
 * backend so the tier sequence rendered here is the one it enforces.
 *
 * Keep this in step with the Scala: the API is what actually decides a request, so a FE that
 * disagrees either hides a legal action or offers one the API will reject.
 */

export const ENTITY_REQUEST_APPROVED_BY_TYPES = {
	all: 'all',
	any: 'any',
};

/**
 * Whether the request's hierarchy is what will actually decide its next transition, rather than a
 * record of a review that is over or a snapshot no live rule backs any more.
 *
 * `hasApprovalHierarchy` says the tiers are worth rendering; this says they are worth enforcing. The
 * two diverge once the rule is deleted or every one of its approvers is deactivated — the API keeps
 * returning the tier history but falls back to the flat `canApproveEntityRequests` permission — so
 * every action gate reads this one, or it would refuse an action the API allows.
 *
 * `isApprovalHierarchyEnforced` has to be *explicitly* false to disable enforcement. An API that
 * predates the field omits it entirely, and reading that absence as "no hierarchy" would silently
 * turn every gate back into the flat permission — which is how an approver who had already signed
 * still got an enabled Approve button. Absent therefore means "assume the tiers still gate this",
 * which is the answer for every request except the two divergent cases above.
 */
export const isEntityRequestApprovalHierarchyEnforced = (record) =>
	// Both flags are read off the raw record: checkHasApprovalHierarchy hands back the unconverted
	// value (hence the `!!`), and nullSafeGet collapses `false` to undefined, so it cannot tell an API
	// that said "not enforcing" from one that has never heard of the field.
	!!checkHasApprovalHierarchy(record) && record.isApprovalHierarchyEnforced !== false;

const emailsOf = (users) =>
	nullSafeGetOrElse('length', users, 0)
		? users.map((user) => nullSafeGet('email', user)).filter((email) => !!email)
		: [];

const approverEmailsOf = (tier) => emailsOf(nullSafeGetOrElse('approvers', tier, []));

const signatureEmailsOf = (tier, signatureField) =>
	emailsOf(nullSafeGetOrElse(signatureField, tier, []));

const includesEmail = (emails, email) =>
	!!email && emails.some((candidate) => compareStrings(candidate, email));

// Tiers with no amount sort last, as on the backend, so an amount-less entity-request hierarchy
// falls through to ordering by label — which is the order approvals are expected in.
const tierAmountOrder = (tier) => {
	const amount = parseFloat(nullSafeGet('amountUpto', tier));
	return Number.isNaN(amount) ? infinityButNumber : amount;
};

const tierLabelOrder = (tier) => {
	const label = parseInt(nullSafeGet('label', tier), 10);
	return Number.isNaN(label) ? infinityButNumber : label;
};

export const sortEntityRequestTiers = (tiers) =>
	[...tiers].sort(
		(a, b) => tierAmountOrder(a) - tierAmountOrder(b) || tierLabelOrder(a) - tierLabelOrder(b)
	);

/** Tiers that can actually gate the request — a tier with no approver emails is a no-op. */
export const entityRequestTiersWithApprovers = (tiers) =>
	sortEntityRequestTiers(tiers).filter((tier) => approverEmailsOf(tier).length > 0);

export const isEntityRequestTierSatisfied = (tier) => {
	const requiredEmails = approverEmailsOf(tier);
	if (requiredEmails.length === 0) {
		return true;
	}
	const approvedEmails = signatureEmailsOf(tier, 'approvedByUsers');
	return compareStrings(
		nullSafeGetOrElse('approvedByType', tier, ''),
		ENTITY_REQUEST_APPROVED_BY_TYPES.all
	)
		? requiredEmails.every((email) => includesEmail(approvedEmails, email))
		: requiredEmails.some((email) => includesEmail(approvedEmails, email));
};

/**
 * True once the highest-ordered tier holding approvers has signed off — "highest tier wins", so a
 * senior approver completes the request and the tiers below them become optional. Mirrors the
 * backend's `isFullyApproved`, including its use of the single last tier in sort order rather than a
 * group of joint-highest tiers. False when no tier holds approvers — nothing gates the request.
 */
export const isEntityRequestFullyApproved = (tiers) => {
	const gatingTiers = entityRequestTiersWithApprovers(tiers);
	const highestGatingTier = gatingTiers[gatingTiers.length - 1];
	return !!highestGatingTier && isEntityRequestTierSatisfied(highestGatingTier);
};

/** The lowest-ordered gating tier still awaiting sign-off — who the request is waiting on. */
export const firstPendingEntityRequestTier = (tiers) =>
	entityRequestTiersWithApprovers(tiers).find((tier) => !isEntityRequestTierSatisfied(tier));

/** Approvers on a tier who have not signed off yet, as approver objects so contacts stay renderable. */
export const pendingEntityRequestApprovers = (tier) => {
	const approvedEmails = signatureEmailsOf(tier, 'approvedByUsers');
	return nullSafeGetOrElse('approvers', tier, []).filter((approver) => {
		const email = nullSafeGet('email', approver);
		return !!email && !includesEmail(approvedEmails, email);
	});
};

/**
 * One approver rendered as a human-readable name, falling back to their email.
 *
 * getContactName space-joins the name parts, so an unresolved contact yields " " rather than an
 * empty string — the trim is what lets the email fallback fire.
 */
export const describeEntityRequestApprover = (approver) =>
	getContactName(nullSafeGet('contact', approver)).trim() || nullSafeGet('email', approver);

export const isEntityRequestApprover = (tiers, email) =>
	!!email && tiers.some((tier) => includesEmail(approverEmailsOf(tier), email));

/** True when every tier that lists this approver already carries their sign-off. */
export const hasEntityRequestApprovalFrom = (tiers, email) => {
	const listingTiers = tiers.filter((tier) => includesEmail(approverEmailsOf(tier), email));
	return (
		listingTiers.length > 0 &&
		listingTiers.every((tier) => includesEmail(signatureEmailsOf(tier, 'approvedByUsers'), email))
	);
};

/**
 * The sequential-order check: true when a tier the approver is not on is still awaiting sign-off.
 *
 * Phrased as "is anything blocking" rather than "is the approver on the pending tier" because those
 * differ once nothing is pending — with no outstanding tier there is no earlier sign-off to wait for,
 * so nobody is blocked. Same wording as the backend for the same reason.
 */
export const isBlockedByEarlierEntityRequestTier = (tiers, email) => {
	const pendingTier = firstPendingEntityRequestTier(tiers);
	return !!pendingTier && !includesEmail(approverEmailsOf(pendingTier), email);
};

/**
 * The tiers as they would stand after `email` approves. The signature lands on every tier that lists
 * the approver — one person sitting on two tiers signs both at once, per the backend — which is what
 * makes the "would this be the final approval" answer below match what the API will do.
 */
export const withEntityRequestApprovalRecorded = (tiers, email) =>
	tiers.map((tier) => {
		const approvedByUsers = nullSafeGetOrElse('approvedByUsers', tier, []);
		const listsApprover = includesEmail(approverEmailsOf(tier), email);
		const alreadySigned = includesEmail(signatureEmailsOf(tier, 'approvedByUsers'), email);
		return !listsApprover || alreadySigned
			? tier
			: { ...tier, approvedByUsers: [{ email }, ...approvedByUsers] };
	});

/**
 * Whether this user's approval would be the last one needed — i.e. whether the proposed entity gets
 * created on it. A partial approval only records their tier's sign-off.
 */
export const willEntityRequestBeFullyApproved = (tiers, email) =>
	isEntityRequestFullyApproved(withEntityRequestApprovalRecorded(tiers, email));

/** Tier heading used by the approvers modal, reused wherever a tier is named in prose. */
export const getEntityRequestTierDisplayName = (tier) => {
	const label = nullSafeGetOrElse('label', tier, '1');
	const name = nullSafeGet('name', tier);
	return name ? `${name} (Tier ${label})` : `Tier ${label}`;
};

/**
 * Why an action gate said no, so a caller can treat the cases differently — a spent action is worth
 * hiding outright, whereas a user who was never eligible needs the button plus an explanation.
 */
export const ENTITY_REQUEST_GATE_BLOCKERS = {
	noPermission: 'noPermission',
	notApprover: 'notApprover',
	alreadyApproved: 'alreadyApproved',
	earlierTierPending: 'earlierTierPending',
};

/**
 * Whether the current user may approve, mirroring the backend's `authorizeReview` with
 * `enforceTierOrder = true`. Without a hierarchy the flat `canApproveEntityRequests` permission
 * decides, exactly as before this hierarchy existed.
 *
 * The already-approved case is a FE-only guard: the backend tolerates a repeat approval as a no-op,
 * but offering a button that does nothing is worse than explaining why it is disabled.
 */
export const getEntityRequestApproveGate = ({
	hasApprovalHierarchy,
	tiers,
	currentUserEmail,
	sequentialApprovalEnforcement,
	defaultActionsAllowed,
}) => {
	if (!hasApprovalHierarchy) {
		return defaultActionsAllowed
			? { allowed: true, reason: undefined, blocker: undefined }
			: {
					allowed: false,
					reason: 'You are not allowed to approve this request.',
					blocker: ENTITY_REQUEST_GATE_BLOCKERS.noPermission,
			  };
	}
	if (!isEntityRequestApprover(tiers, currentUserEmail)) {
		return {
			allowed: false,
			reason: 'You are not an approver for this request.',
			blocker: ENTITY_REQUEST_GATE_BLOCKERS.notApprover,
		};
	}
	if (hasEntityRequestApprovalFrom(tiers, currentUserEmail)) {
		return {
			allowed: false,
			reason: 'You have already approved this request.',
			blocker: ENTITY_REQUEST_GATE_BLOCKERS.alreadyApproved,
		};
	}
	if (
		sequentialApprovalEnforcement &&
		isBlockedByEarlierEntityRequestTier(tiers, currentUserEmail)
	) {
		const pendingTier = firstPendingEntityRequestTier(tiers);
		return {
			allowed: false,
			reason: `Sequential approval is enforced. ${getEntityRequestTierDisplayName(
				pendingTier
			)} must approve first.`,
			blocker: ENTITY_REQUEST_GATE_BLOCKERS.earlierTierPending,
		};
	}
	return { allowed: true, reason: undefined, blocker: undefined };
};

/**
 * Whether the current user may reject. A rejection ends the review outright, so — as on the backend,
 * which calls `authorizeReview` with `enforceTierOrder = false` — any approver on the hierarchy may
 * reject from any tier, including one who has already approved and changed their mind.
 */
export const getEntityRequestRejectGate = ({
	hasApprovalHierarchy,
	tiers,
	currentUserEmail,
	defaultActionsAllowed,
}) => {
	if (!hasApprovalHierarchy) {
		return defaultActionsAllowed
			? { allowed: true, reason: undefined, blocker: undefined }
			: {
					allowed: false,
					reason: 'You are not allowed to reject this request.',
					blocker: ENTITY_REQUEST_GATE_BLOCKERS.noPermission,
			  };
	}
	return isEntityRequestApprover(tiers, currentUserEmail)
		? { allowed: true, reason: undefined, blocker: undefined }
		: {
				allowed: false,
				reason: 'You are not an approver for this request.',
				blocker: ENTITY_REQUEST_GATE_BLOCKERS.notApprover,
		  };
};

const dedupeSignaturesByEmail = (users) =>
	users.filter(
		(user, index, allUsers) =>
			index === allUsers.findIndex((other) => compareStrings(other.email, user.email))
	);

/**
 * Sorts and annotates tiers for the shared approvers modal, the entity-request counterpart of
 * `buildApprovalTierView`.
 *
 * Every tier is visible: the NTE trimming the invoice / quote view performs has no amounts to act on
 * here, and every tier gates the request regardless. `showPendingApprovers` keeps a tier's
 * outstanding approvers listed once someone has signed, which an `all` tier needs — the modal
 * otherwise collapses that list on the first signature, and on an `all` tier the approvers who have
 * not signed are exactly the ones still holding the request up.
 */
export const buildEntityRequestTierView = (rawTiers) =>
	sortEntityRequestTiers(nullSafeGetOrElse('length', rawTiers, 0) ? rawTiers : []).map((tier) => ({
		...tier,
		approvedByUsers: dedupeSignaturesByEmail(nullSafeGetOrElse('approvedByUsers', tier, [])),
		declinedByUsers: dedupeSignaturesByEmail(nullSafeGetOrElse('declinedByUsers', tier, [])),
		isVisible: true,
		showPendingApprovers: !isEntityRequestTierSatisfied(tier),
	}));

/**
 * Whether this user may act on a specific request at all — the gate the Review affordances read.
 *
 * With an enforcing hierarchy the tiers decide and the flat `canApproveEntityRequests` permission is
 * irrelevant, which is how the backend's `authorizeReview` reads it: tier membership is the grant.
 * The permission only stands in when no hierarchy governs the request. Answering per record rather
 * than per page is the point — the tiers are only known here, and a page-level flat permission hid
 * Review from the very approvers a hierarchy named (OPE-5331).
 *
 * The API strips the requester out of the tiers it returns, so a requester never reads as an approver
 * on their own request and the four-eyes rule needs no restating here.
 */
export const canReviewEntityRequest = (record, currentUserEmail, defaultActionsAllowed) =>
	isEntityRequestApprovalHierarchyEnforced(record)
		? isEntityRequestApprover(
				nullSafeGetOrElse('approvalHierarchy.tierDetails', record, []),
				currentUserEmail
		  )
		: !!defaultActionsAllowed;
